import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useEscapeToClose from '../utilities/useEscapeToClose.js';

const ProjectDetailModal = ({ project, onClose }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const closeButtonRef = useRef(null);
  const images = project?.images || [];
  const activeImage = images[activeImageIndex] || null;

  useEffect(() => {
    setActiveImageIndex(0);
  }, [project?.id]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  useEscapeToClose(true, onClose);

  const changeImage = (offset) => {
    if (images.length < 2) return;
    setActiveImageIndex((current) => (current + offset + images.length) % images.length);
  };

  return (
    <div
      className="project-detail-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="project-detail-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-detail-title"
      >
        <header className="project-detail-header">
          <div className="project-detail-heading">
            <small>Project details</small>
            <h1 id="project-detail-title">{project.title}</h1>
          </div>
          <button
            ref={closeButtonRef}
            className="btn btn-light btn-sm project-detail-close"
            type="button"
            onClick={onClose}
            aria-label="Close project details"
            title="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        <div className="project-detail-content">
          <div className="project-detail-copy">
            <p>{project.description || 'No project description is available.'}</p>
            {project.skills?.length ? (
              <section className="project-detail-skills" aria-labelledby="project-detail-skills-title">
                <h2 id="project-detail-skills-title">Skills used</h2>
                <div>
                  {project.skills.map((skill) => (
                    <span className="project-skill" key={skill.id}>
                      {skill.imageSrc ? (
                        <img className="project-skill-icon" src={skill.imageSrc} alt="" aria-hidden="true" />
                      ) : (
                        <span className="project-skill-icon" aria-hidden="true">
                          <i className={`bi ${skill.iconClass || 'bi-award'}`} />
                        </span>
                      )}
                      {skill.name}
                    </span>
                  ))}
                </div>
              </section>
            ) : null}
            {project.href ? (
              <a
                className="btn btn-primary"
                href={project.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-box-arrow-up-right me-2" aria-hidden="true" />
                Open project
              </a>
            ) : null}
          </div>

          <div className="project-detail-gallery" aria-label="Project images">
            {activeImage ? (
              <>
                <figure className="project-detail-image">
                  <img src={activeImage.src} alt={activeImage.alt} />
                  {activeImage.description ? <figcaption>{activeImage.description}</figcaption> : null}
                </figure>

                {images.length > 1 ? (
                  <div className="project-detail-gallery-controls">
                    <button
                      className="btn btn-light btn-sm"
                      type="button"
                      onClick={() => changeImage(-1)}
                      aria-label="Previous project image"
                    >
                      <i className="bi bi-chevron-left" aria-hidden="true" />
                    </button>
                    <span aria-live="polite">{activeImageIndex + 1} of {images.length}</span>
                    <button
                      className="btn btn-light btn-sm"
                      type="button"
                      onClick={() => changeImage(1)}
                      aria-label="Next project image"
                    >
                      <i className="bi bi-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                ) : null}

                {images.length > 1 ? (
                  <div className="project-detail-thumbnails" aria-label="Choose a project image">
                    {images.map((image, index) => (
                      <button
                        className={index === activeImageIndex ? 'is-active' : ''}
                        type="button"
                        key={image.id}
                        onClick={() => setActiveImageIndex(index)}
                        aria-label={`Show project image ${index + 1}`}
                        aria-pressed={index === activeImageIndex}
                      >
                        <img src={image.src} alt="" />
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="project-detail-no-images">
                <i className="bi bi-image" aria-hidden="true" />
                <span>No project images are available.</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

ProjectDetailModal.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    href: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      alt: PropTypes.string,
      description: PropTypes.string,
    })),
    skills: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      iconClass: PropTypes.string,
      imageSrc: PropTypes.string,
    })),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProjectDetailModal;
